import { deprecation } from '../..';

/**
 * Returns a random color
 * @param random - The random function to be used (defaults to Math.random)
 */
export function randomColor(random = Math.random) {
  const r = Math.floor(0xff * random());
  const g = Math.floor(0xff * random());
  const b = Math.floor(0xff * random());

  return (r << 16) | (g << 8) | b;
}

/**
 * Returns a random number within a range
 * @param min - lowest number (inclusive)
 * @param max - highest number (exclusive)
 * @param floor - whether the number should be floored
 * @param random - The random function to be used (defaults to Math.random)
 */
export function randomRange(
  min: number,
  max: number,
  floor = false,
  random = Math.random,
) {
  const v = min + (max - min) * random();

  return floor ? Math.floor(v) : v;
}

/**
 * Returns a random value from an object
 * @param obj - object to be selected
 * @param random - The random function to be used (defaults to Math.random)
 */
export function randomObjItem<T>(obj: T, random = Math.random): T[keyof T] {
  const keys = Object.keys(obj as any);
  const key = keys[Math.floor(random() * keys.length)];

  return obj[key as keyof T];
}

/**
 * Returns a random item from an array
 * @param arr - array to be selected
 * @param random - The random function to be used (defaults to Math.random)
 */
export function randomItem<T>(arr: T[], random = Math.random) {
  return arr[Math.floor(random() * arr.length)];
}

/**
 * Returns a random number within a range.
 *
 * @param min - The minimum value (inclusive).
 * @param max - The maximum value (exclusive).
 */
export function randomFloat(min: number, max: number, random = Math.random) {
  return random() * (max - min) + min;
}

/**
 * Returns a random integer within a range.
 * @param min - The minimum value (inclusive).
 * @param max - The minimum value (inclusive).
 * @param random - The random function to be used (defaults to Math.random)
 */
export function randomInt(min: number, max: number, random = Math.random) {
  // This function will return 4 if float result is 3.5 because of +1. Should return 3 instead?
  return Math.floor(random() * (max - min + 1)) + min;
}

/**
 * Returns a random boolean.
 * @param weight - The chance of true value, between 0 and 1
 * @param random - The random function to be used (defaults to Math.random)
 * @returns
 */
export function randomBool(weight = 0.5, random = Math.random) {
  return random() < weight;
}

/**
 * Return -1 or 1.
 * @param random - The random function to be used (defaults to Math.random)
 * @returns
 */
export function randomSign(random = Math.random) {
  return randomInt(0, 1, random) * 2 - 1;
}

/**
 * Random shuffle an array in place, without cloning it
 * @param array - The array that will be shuffled
 * @param random - The random function to be used (defaults to Math.random)
 * @returns
 */
export function randomShuffle<T>(array: T[], random = Math.random): T[] {
  let currentIndex = array.length;
  let temporaryValue;
  let randomIndex;

  while (currentIndex !== 0) {
    randomIndex = Math.floor(random() * currentIndex);
    currentIndex -= 1;
    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }

  return array;
}

/**
 * Return a random string hash - not guaranteed to be unique
 * @param length - The length of the hash
 * @param random - The random function to be used (defaults to Math.random)
 * @returns
 */
export function randomHash(length: number, random = Math.random): string {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let result = '';

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(random() * charactersLength));
  }

  return result;
}

/**
 * Pass a list of items with odds value for each, and return a random result based on these odds.
 * Example: `const item = randomOdds([[3, item1], [5, item2], [2, item3]])`
 * @param odds - An array of [odd, item] pairs
 * @param random - The random function to be used (defaults to Math.random)
 * @returns One of the passed items
 */
export function randomOdds<T>(odds: [number, T][], random = Math.random): T {
  if (!odds.length) throw new Error('[Random] Odds list provided is empty');

  // Return the single odd if list has just one item
  if (odds.length === 1) return odds[0][1];

  // Sort item by lower to high odds
  odds = odds.slice(0).sort((a, b) => a[0] - b[0]);

  // Sum of all odds provided
  const amount = odds.reduce((prev, curr) => prev + curr[0], 0);

  // Map odd entries by value range
  const oddsMap: { min: number; max: number; val: T }[] = [];
  let accumulated = 0;

  for (const odd of odds) {
    oddsMap.push({
      min: accumulated,
      max: accumulated + odd[0],
      val: odd[1],
    });

    accumulated += odd[0];
  }

  // Random number based on amount
  const rand = random() * amount;

  for (const odd of oddsMap) {
    if (rand >= odd.min && rand < odd.max) return odd.val;
  }

  // There is something wrong if the function did not return any value until this point
  throw new Error('[Random] Something has gone wrong with the odds provided');
}

/**
 * Returns a random item from the ones provided
 * DEPRECATED - Rest parameters conflicts with the idea of having an optional parameter for randomisation function
 * like all the other methods. Also, `randomItem` does exactly the same thing without rest parameters, so this
 * method should be removed in future.
 * @param args - items to be selected
 */
export function randomPick<T>(...args: T[]) {
  deprecation(
    '12.3.3',
    'The "randomPick" function is going to be removed in future. Use "randomItem" instead.',
  );

  return args[Math.floor(Math.random() * args.length)];
}
